import * as React from "react";
import type { AIDraftResult } from "@eduviaos/capability-ai-ui";
import { api } from "@/lib/api";

/**
 * Client for `POST /api/ai/draft/{kind}` (Phase SC-22e). The BE returns a
 * clamped, validated draft for one create form; nothing is persisted. Shapes
 * mirror `app/domains/ai_draft/schemas.py`.
 */

export interface FeePlanDraft {
  kind: "fee_plan";
  name: string | null;
  description: string | null;
  plan_type: string | null;
  base_amount: number | null;
  billing_cycle: "monthly" | "quarterly" | "semi_annual" | "annual" | "one_time" | null;
  due_day_of_month: number | null;
  allow_partial_payment: boolean | null;
}

export interface EnquiryDraft {
  kind: "enquiry";
  student_name: string | null;
  student_email: string | null;
  student_phone: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  source: "walk_in" | "online_form" | "referral" | "phone" | "ad_campaign" | "other" | null;
  interest: string | null;
  notes: string | null;
}

export interface HolidayDraftItem {
  name: string;
  start_date: string;
  end_date: string | null;
  kind: string;
  description: string | null;
}

export interface HolidayListDraft {
  kind: "holiday_list";
  holidays: HolidayDraftItem[];
}

export interface TriggerRecipientDraft {
  kind: string;
  config: Record<string, string | string[]>;
}

export interface NotificationTriggerDraft {
  kind: "notification_trigger";
  name: string | null;
  description: string | null;
  event: string | null;
  channels: string[];
  recipients: TriggerRecipientDraft[];
  template_slug: string | null;
  message_subject: string | null;
  message_body: string | null;
}

export interface LearnerDraft {
  kind: "learner";
  fields: Record<string, string>;
  custom_fields: Record<string, string>;
}

export interface HelpdeskReplyDraft {
  kind: "helpdesk_reply";
  body: string | null;
}

export interface SubjectDraft {
  kind: "subject";
  code: string | null;
  name: string | null;
  subject_kind: string | null;
  default_periods_per_week: number | null;
  default_period_length_min: number | null;
  applicable_grades: string[];
  requires_lab_room: boolean | null;
  stream_tag: string | null;
}

export interface SectionDraft {
  kind: "section";
  name: string | null;
  grade_ref: string | null;
  max_students: number | null;
  display_order: number | null;
}

export interface AIDraftByKind {
  fee_plan: FeePlanDraft;
  enquiry: EnquiryDraft;
  holiday_list: HolidayListDraft;
  notification_trigger: NotificationTriggerDraft;
  learner: LearnerDraft;
  helpdesk_reply: HelpdeskReplyDraft;
  subject: SubjectDraft;
  section: SectionDraft;
}

export type AIDraftKind = keyof AIDraftByKind;

export interface AIDraftResponse<K extends AIDraftKind> {
  kind: K;
  draft: AIDraftByKind[K];
  explanation: string;
  warnings: string[];
  provider: string;
  model: string;
  generated_at: string;
}

export interface AIDraftContext {
  ticket_id?: string;
}

export async function requestAIDraft<K extends AIDraftKind>(
  kind: K,
  prompt: string,
  context?: AIDraftContext,
): Promise<AIDraftResult<AIDraftByKind[K]>> {
  const res = await api.post<AIDraftResponse<K>>(`/api/ai/draft/${kind}`, {
    prompt,
    context: context ?? {},
  });
  return { draft: res.draft, explanation: res.explanation, warnings: res.warnings };
}

/** Stable `generate` callback for `AIDraftButton`. */
export function useAIDraftGenerator<K extends AIDraftKind>(
  kind: K,
  context?: AIDraftContext,
): (prompt: string) => Promise<AIDraftResult<AIDraftByKind[K]>> {
  const ticketId = context?.ticket_id;
  return React.useCallback(
    (prompt: string) => requestAIDraft(kind, prompt, ticketId ? { ticket_id: ticketId } : {}),
    [kind, ticketId],
  );
}

export function draftRow(label: string, value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return [];
  return [{ label, value: String(value) }];
}
